import { useMemo, useCallback } from 'react'
import moment from 'moment'

import { buildSuggestedDatesParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { logNewRelic } from '../../services/newRelic'
import { useAppSelector } from 'hooks/reduxHooks'
import useOfferMetaData from '../Offers/useOfferMetaData'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { determineMemberPricing, onlyNonZeroFees } from 'lib/offer/pricing'
import { isBundlePackageWithPrice } from 'lib/offer/offerTypes'
import useFlightAndHotelBundle from '../FlightAndHotelBundle/useFlightAndHotelBundle'
import { getIsSpecificCampaignPage } from 'selectors/routerSelectors'
import { CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'

interface Props {
  offer: App.Offer | App.OfferSummary
  pkg?: App.Package | App.BundlePackageWithPrice
  rate?: App.OfferAvailableRate
  duration?: number
  filters?: App.OfferListFilters
  useRealSearchPrices?: boolean // use prices directly from search api
  showPerNightPricing?: boolean
}

interface UseSearchPricesResult {
  /** @deprecated - use defaultPricing` instead */
  totalPrice: number
  /** @deprecated - use memberPricing instead */
  totalMemberPrice?: number
  /** @deprecated - use defaultPricing instead */
  totalValueBase: number
  /** @deprecated - use defaultPricing` instead */
  discountPercent: number
  hotelPrice: number
  hotelMemberPrice: number
  showMemberPrice: boolean
  propertyFees: number
  suggestedDates?: App.OfferSuggestedDates
  defaultPricing: App.PricingWithValue
  memberPricing?: App.PricingWithValue
  /** Bundle pricing (hotel + flight) when bundle mode is enabled - total price */
  bundlePricing?: App.PricingWithValue & { bundleAddonPrice: number }
  bundleMemberPricing?: App.PricingWithValue & { bundleAddonPrice: number }
  /** Bundle pricing per person */
  bundlePricingPerPerson?: App.PricingWithValue & { bundleAddonPrice: number }
  bundleMemberPricingPerPerson?: App.PricingWithValue & { bundleAddonPrice: number }
}

export function useDirectSearchPrices({ filters, offerId }: { filters: App.OfferListFilters, offerId: string }): App.OfferListMetadataPricing | undefined {
  const offerMetadata = useOfferMetaData(offerId, filters)
  if (getShouldUseSearchPrices(filters)) return offerMetadata?.pricing
}

function calculatePerNightPrice(price: number, duration: number = 1) {
  return Math.round(price / duration)
}

export function useSearchPrices({ offer, pkg, rate, filters, useRealSearchPrices = false, showPerNightPricing = false }: Props): UseSearchPricesResult {
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const offerMetadata = useOfferMetaData(offer.id, filters)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(filters?.flexibleMonths, filters?.flexibleNights, filters?.rooms)
  const suggestedDates = useAppSelector((state) => getSuggestedDates(state, flexibleSearchFilterKey, offer.id))
  const isFlexibleSearch = filters?.flexibleMonths || filters?.flexibleNights

  // Flight and Hotel bundle data
  const flightAndHotelBundle = useFlightAndHotelBundle()
  const isMaldivesCharter = useAppSelector((state: App.State) => getIsSpecificCampaignPage(state, CHARTER_PAGE_SLUG))

  // Verify dates match for bundle calculation
  const datesMatch = flightAndHotelBundle.selectedDates.checkIn === filters?.checkIn && flightAndHotelBundle.selectedDates.checkOut === filters?.checkOut

  // Calculate bundle addon price
  const bundleAddonPrice = useMemo(() => {
    const addonPrice = (isMaldivesCharter && datesMatch && flightAndHotelBundle.flightData.totalPrice)
      ? flightAndHotelBundle.flightData.totalPrice
      : 0

    return addonPrice
  }, [isMaldivesCharter, datesMatch, flightAndHotelBundle.flightData.totalPrice])

  // Helper function to add flight price to existing pricing
  const addFlightPriceToExistingPricing = useCallback((pricing: App.PricingWithValue, addonPrice: number): App.PricingWithValue & { bundleAddonPrice: number } => {
    const newPrice = pricing.price + addonPrice
    return {
      ...pricing,
      price: newPrice,
      discountPercent: getValueOffPercent(pricing.value, newPrice),
      bundleAddonPrice: addonPrice,
    }
  }, [])
  const rateOrPkg = (rate ?? pkg)
  const baseMemberPrice = rateOrPkg?.memberPrice || 0
  const showMemberPrice = luxPlusEnabled && baseMemberPrice > 0
  const rateOrPkgMemberPrice = rate?.memberPriceWithSurcharge ?? pkg?.memberPrice
  const rooms = useMemo(() => filters?.rooms || [], [filters?.rooms])

  // Calculate total passengers for per-person pricing
  const totalPassengers = useMemo(
    () => sumUpOccupancies(rooms),
    [rooms]
  )

  // Helper function to convert pricing to per-person (always converts)
  const applyPerPersonPricing = useCallback((pricing: App.PricingWithValue & { bundleAddonPrice: number }): App.PricingWithValue & { bundleAddonPrice: number } => {
    if (totalPassengers === 0) {
      return pricing
    }

    return {
      ...pricing,
      price: pricing.price / totalPassengers,
      value: pricing.value / totalPassengers,
    }
  }, [totalPassengers])
  const priceMultiplier = rate ? 1 : (rooms.length || 1)
  const propertyFees = (rate?.propertyFees ?? pkg?.propertyFees ?? 0) * priceMultiplier
  const extraGuestSurcharges = useMemo(() => {
    if (!pkg) return 0

    if (isBundlePackageWithPrice(pkg)) {
      return 0
    }
    return getSumExtraGuestsSurcharge(rooms, offer, pkg)
  }, [pkg, rooms, offer])
  const hotelPrice = ((rateOrPkg?.price ?? 0) + (pkg?.surcharge ?? 0)) * priceMultiplier + extraGuestSurcharges
  const hotelMemberPrice = showMemberPrice ? ((rateOrPkgMemberPrice ?? 0 + (pkg?.surcharge ?? 0)) * priceMultiplier + extraGuestSurcharges) : 0
  const totalPrice = isFlexibleSearch && suggestedDates?.price ? suggestedDates?.price : hotelPrice + propertyFees
  const pkgValueOrMemberValue = canViewLuxPlusBenefits ? pkg?.memberValue : pkg?.value
  const totalValueBase = rate?.hotelValue ?? suggestedDates?.value ?? (pkgValueOrMemberValue ?? 0 * (rooms.length || 1) + extraGuestSurcharges)
  const discountPercent = pkg?.discountPercent || getValueOffPercent(totalValueBase, showMemberPrice && canViewLuxPlusBenefits ? hotelMemberPrice : hotelPrice)
  const surcharge = (rateOrPkg?.surcharge ?? 0) * priceMultiplier
  const taxesAndFees = (rateOrPkg?.taxesAndFees ?? 0) * priceMultiplier

  const totalMemberPrice = useMemo(() => {
    if (showMemberPrice) {
      let memberPrice: number
      if (isFlexibleSearch && suggestedDates?.memberPrice) {
        memberPrice = suggestedDates.memberPrice
      } else {
        memberPrice = hotelMemberPrice + propertyFees
      }
      return memberPrice
    }
  }, [showMemberPrice, isFlexibleSearch, suggestedDates, hotelMemberPrice, propertyFees])

  return useMemo<UseSearchPricesResult>(() => {
    const fees = onlyNonZeroFees([
      { type: 'property', amount: propertyFees },
      { type: 'taxesAndFees', amount: taxesAndFees },
      { type: 'surcharge', amount: surcharge },
      { type: 'extraGuestSurcharge', amount: extraGuestSurcharges },
    ])

    if (useRealSearchPrices) {
      if (!offerMetadata?.pricing) {
        logNewRelic('No pricing found for offer from search', { offerId: offer.id, filters }, 'error')
        return {
          totalPrice: 0,
          totalMemberPrice: 0,
          totalValueBase: 0,
          discountPercent: 0,
          hotelPrice: 0,
          hotelMemberPrice: 0,
          showMemberPrice: false,
          propertyFees: 0,
          suggestedDates,
          defaultPricing: {
            price: 0,
            value: 0,
            discountPercent: 0,
            saleUnit: offer.saleUnit,
            fees: [],
          },
          memberPricing: undefined,
          bundlePricing: undefined,
          bundleMemberPricing: undefined,
          bundlePricingPerPerson: undefined,
          bundleMemberPricingPerPerson: undefined,
        }
      }

      const fallbackPrice = offer.lowestPricePackage
      const pricing = offerMetadata?.pricing

      // For charter bundle, use actual package data instead of metadata pricing
      // Metadata pricing is inaccurate because backend doesn't receive checkIn/checkOut params
      let actualPackage = pkg
      if (filters?.charterBundle && filters.checkIn && filters.checkOut && offer.packages) {
        const requestedDuration = moment(filters.checkOut).diff(filters.checkIn, 'days')
        const matchingPackage = offer.packages.find(p => p.duration === requestedDuration)
        if (matchingPackage) {
          actualPackage = matchingPackage
        }
      }

      const discountPercent = (() => {
        // For charter bundle, use actual package values
        if (filters?.charterBundle && actualPackage) {
          return getValueOffPercent(actualPackage.value ?? 0, actualPackage.price ?? 0)
        }
        if (showMemberPrice && canViewLuxPlusBenefits && pricing.lowestMemberPrice !== undefined && pricing.lowestPriceValue !== undefined) {
          return getValueOffPercent(pricing.lowestPriceValue, pricing.lowestMemberPrice)
        }
        if (pricing.lowestPrice && pricing.lowestPriceValue !== undefined) {
          return getValueOffPercent(pricing.lowestPriceValue, pricing.lowestPrice)
        }
        return getValueOffPercent(fallbackPrice?.value ?? 0, fallbackPrice?.price ?? 0)
      })()

      // For charter bundle, use actual package prices instead of metadata
      const totalPrice = filters?.charterBundle && actualPackage
        ? (actualPackage.price ?? 0)
        : (pricing.lowestPrice ?? fallbackPrice?.price ?? 0)
      const totalMemberPrice = filters?.charterBundle && actualPackage
        ? (actualPackage.memberPrice ?? 0)
        : (pricing?.lowestMemberPrice ?? fallbackPrice?.memberPrice ?? 0)
      const totalValueBase = filters?.charterBundle && actualPackage
        ? (actualPackage.value ?? 0)
        : (pricing?.lowestPriceValue ?? fallbackPrice?.value ?? 0)

      const defaultPricing: App.PricingWithValue = {
        price: totalPrice,
        value: totalValueBase,
        discountPercent: getValueOffPercent(totalValueBase, totalPrice),
        saleUnit: offer.saleUnit,
        fees,
      }

      return {
        totalPrice: showPerNightPricing ? calculatePerNightPrice(totalPrice, pkg?.duration) : totalPrice,
        totalMemberPrice,
        totalValueBase,
        discountPercent,
        hotelPrice: filters?.charterBundle && actualPackage ? (actualPackage.price ?? 0) : (pricing.lowestPrice ?? fallbackPrice?.price ?? 0),
        hotelMemberPrice: filters?.charterBundle && actualPackage ? (actualPackage.memberPrice ?? 0) : (pricing.lowestMemberPrice ?? fallbackPrice?.memberPrice ?? 0),
        showMemberPrice,
        propertyFees: filters?.charterBundle && actualPackage ? (actualPackage.propertyFees ?? 0) : (pricing.lowestPricePropertyFees ?? fallbackPrice?.propertyFees ?? 0),
        suggestedDates,
        defaultPricing,
        memberPricing: determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase),
        // Bundle pricing: add flight price to existing hotel pricing (total price)
        bundlePricing: bundleAddonPrice > 0 ? addFlightPriceToExistingPricing(defaultPricing, bundleAddonPrice) : undefined,
        bundleMemberPricing: bundleAddonPrice > 0 && determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)
          ? addFlightPriceToExistingPricing(determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)!, bundleAddonPrice)
          : undefined,
        // Bundle pricing per person
        bundlePricingPerPerson: bundleAddonPrice > 0 ? applyPerPersonPricing(addFlightPriceToExistingPricing(defaultPricing, bundleAddonPrice)) : undefined,
        bundleMemberPricingPerPerson: bundleAddonPrice > 0 && determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)
          ? applyPerPersonPricing(addFlightPriceToExistingPricing(determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)!, bundleAddonPrice))
          : undefined,
      }
    }

    const defaultPricing: App.PricingWithValue = {
      price: showPerNightPricing ? calculatePerNightPrice(totalPrice, pkg?.duration) : totalPrice,
      value: totalValueBase,
      discountPercent: getValueOffPercent(totalValueBase, totalPrice),
      saleUnit: offer.saleUnit,
      fees,
    }

    return {
      totalPrice: showPerNightPricing ? calculatePerNightPrice(totalPrice, pkg?.duration) : totalPrice,
      totalMemberPrice,
      totalValueBase,
      discountPercent,
      hotelPrice,
      hotelMemberPrice,
      showMemberPrice,
      propertyFees,
      suggestedDates,
      defaultPricing,
      memberPricing: determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase),
      // Bundle pricing: add flight price to existing hotel pricing (total price)
      bundlePricing: bundleAddonPrice > 0 ? addFlightPriceToExistingPricing(defaultPricing, bundleAddonPrice) : undefined,
      bundleMemberPricing: bundleAddonPrice > 0 && determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)
        ? addFlightPriceToExistingPricing(determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)!, bundleAddonPrice)
        : undefined,
      // Bundle pricing per person
      bundlePricingPerPerson: bundleAddonPrice > 0 ? applyPerPersonPricing(addFlightPriceToExistingPricing(defaultPricing, bundleAddonPrice)) : undefined,
      bundleMemberPricingPerPerson: bundleAddonPrice > 0 && determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)
        ? applyPerPersonPricing(addFlightPriceToExistingPricing(determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase)!, bundleAddonPrice))
        : undefined,
    }
  }, [canViewLuxPlusBenefits, discountPercent, extraGuestSurcharges, filters, hotelMemberPrice, hotelPrice, offer.id, offer.lowestPricePackage, offer.saleUnit, offer.packages, offerMetadata?.pricing, propertyFees, showMemberPrice, suggestedDates, surcharge, taxesAndFees, totalMemberPrice, totalPrice, totalValueBase, useRealSearchPrices, pkg, showPerNightPricing, bundleAddonPrice, addFlightPriceToExistingPricing, applyPerPersonPricing])
}
